import type * as React from 'react'
import type { ReactNode } from 'react'
import type { NavLinkProps } from 'react-router-dom'
import { NavLink as RouterNavLink, useLocation } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { useBrandingInfo, useFeatures } from '@jotta/grpc-connect-client/config'
import { useGetCustomer } from '@jotta/grpc-connect-client/customer'
import type { OnboardingPopupKeys } from '@jotta/grpc-js-client/customerService'
import { AccountType } from '@jotta/grpc-js-client/customerService'
import { showIntercom } from '@jotta/intercom'
import type { BrandThemeIcon } from '@jotta/types/Brand'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { Onboarding } from '@jotta/ui/Onboarding'
import SvgAlbum from '@jotta/ui/SvgAlbum'
import SvgArchive from '@jotta/ui/SvgArchive'
import SvgBackup from '@jotta/ui/SvgBackup'
import SvgBusinessMenuItem from '@jotta/ui/SvgBusinessMenuItem'
import SvgEyeSlash from '@jotta/ui/SvgEyeSlash'
import SvgGift from '@jotta/ui/SvgGift'
import SvgHelpMenuItem from '@jotta/ui/SvgHelpMenuItem'
import SvgPhotos from '@jotta/ui/SvgPhotos'
import SvgSettingsMenuItem from '@jotta/ui/SvgSettingsMenuItem'
import SvgSharedFolder from '@jotta/ui/SvgSharedFolder'
import SvgStar from '@jotta/ui/SvgStar'
import SvgSync from '@jotta/ui/SvgSync'
import SvgTrash from '@jotta/ui/SvgTrash'
import { Breakpoint, useBreakpoint } from '@jotta/ui/useBreakpoint'
import { cn } from '@jotta/utils/css'

function NavSection({
  children,
  title,
}: {
  children: ReactNode
  title?: string
}) {
  return (
    <div className="flex w-fit flex-col pb-3 lg:w-full lg:px-3">
      {title ? (
        <header className="label-sm whitespace-nowrap pb-2 pl-4 pt-3 leading-5 text-grey4 lg:pl-1">
          {title}
        </header>
      ) : (
        <div className="h-5" />
      )}
      <ul>{children}</ul>
    </div>
  )
}

function NavLink({
  active,
  className,
  icon,
  children,
  ...rest
}: NavLinkProps & {
  active: boolean
  children: React.ReactNode
  icon: BrandThemeIcon
}) {
  return (
    <li>
      <RouterNavLink
        className={cn(
          'btn flex w-full justify-start gap-3 border-none py-2 pl-4 pr-3 font-[450] hover:bg-surface-container-low focus:-outline-offset-2',
          {
            'btn-tonal gap-3 border-0 pl-4': active,
          },
          className,
        )}
        {...rest}
      >
        <PlainBrandIcon className="shrink-0" icon={icon} height={20} />
        {children}
      </RouterNavLink>
    </li>
  )
}

type NavBarProps = React.ComponentProps<'nav'> & {
  onNavigate?: () => void
  onboardingEnabled?: boolean
}

export const NavBar = observer<NavBarProps>(function NavBar({
  className,
  onNavigate = () => null,
  onboardingEnabled = true,
}) {
  const { supportLink = '' } = useBrandingInfo()
  const customer = useGetCustomer().data
  const businessName = customer?.business?.name
  const accountType = customer?.accountType
  const promoteUpgrade = customer?.promoteUpgrade
  const { referralsEnabled } = useFeatures()
  const showNavbar = useBreakpoint(Breakpoint.lg)
  const { pathname } = useLocation()
  const settingsButtonIsActive =
    /^\/web\/(account|secure|billing|family|connected)/.test(pathname)
  const trashButtonIsActive = /^\/web\/(trash)/.test(pathname)
  const bizButtonIsActive = /^\/(biz)/.test(pathname)
  const helpButtonIsActive =
    !showNavbar && (Boolean(customer?.intercom) || supportLink)
  const upgradeButtonIsActive = /^\/web\/(upgrade)/.test(pathname)
  const referIsActive = /^\/web\/(refer-friend)/.test(pathname)

  return (
    <nav className={className}>
      <NavSection title={t`Files`}>
        <ConditionalOnboarding task="files" enabled={onboardingEnabled}>
          <NavLink
            onClick={onNavigate}
            data-section="files"
            to="/web/sync"
            active={pathname.startsWith('/web/sync')}
            icon={SvgSync}
          >
            <Trans>Sync</Trans>
          </NavLink>
        </ConditionalOnboarding>

        <NavLink
          onClick={onNavigate}
          data-section="files"
          to="/web/backup"
          active={pathname.startsWith('/web/backup')}
          icon={SvgBackup}
        >
          <Trans>Backup</Trans>
        </NavLink>

        <NavLink
          onClick={onNavigate}
          data-section="files"
          to="/web/archive"
          active={pathname.startsWith('/web/archive')}
          icon={SvgArchive}
        >
          <Trans>Archive</Trans>
        </NavLink>
      </NavSection>

      <NavSection title={t`Media`}>
        <ConditionalOnboarding task="photos" enabled={onboardingEnabled}>
          <NavLink
            onClick={onNavigate}
            data-section="photos"
            to="/photo"
            icon={SvgPhotos}
            active={pathname === '/photo'}
            end
          >
            <Trans>Photos</Trans>
          </NavLink>
        </ConditionalOnboarding>
        <NavLink
          onClick={onNavigate}
          data-section="photos"
          to="/photo/album"
          icon={SvgAlbum}
          active={pathname.startsWith('/photo/album')}
        >
          <Trans>Collections</Trans>
        </NavLink>
        <NavLink
          onClick={onNavigate}
          data-section="photos"
          to="/photo/all_hidden"
          icon={SvgEyeSlash}
          active={pathname.startsWith('/photo/all_hidden')}
        >
          <Trans>Hidden</Trans>
        </NavLink>
      </NavSection>

      <NavSection title={t`Shared`}>
        <NavLink
          onClick={onNavigate}
          data-section="shared_files"
          to="/web/shared"
          icon={SvgSharedFolder}
          active={pathname.startsWith('/web/shared')}
        >
          <Trans>Shared Files</Trans>
        </NavLink>
        <NavLink
          onClick={onNavigate}
          data-section="shared_photos"
          to="/photo/all_shared"
          icon={SvgSharedFolder}
          active={pathname.startsWith('/photo/all_shared')}
        >
          <Trans>Shared Photos</Trans>
        </NavLink>
      </NavSection>

      <NavSection>
        <ConditionalOnboarding task="trash" enabled={onboardingEnabled}>
          <NavLink
            onClick={onNavigate}
            data-section="trash"
            to="/web/trash"
            icon={SvgTrash}
            active={trashButtonIsActive}
          >
            <Trans>Trash</Trans>
          </NavLink>
        </ConditionalOnboarding>

        {helpButtonIsActive && (
          <NavLink
            active={false}
            onClick={e => {
              onNavigate()
              showIntercom(e)
            }}
            data-section="help"
            to={supportLink}
            icon={SvgHelpMenuItem}
          >
            <Trans>Help</Trans>
          </NavLink>
        )}

        {promoteUpgrade && (
          <NavLink
            onClick={onNavigate}
            data-section="upgrade"
            to="/web/upgrade"
            icon={SvgStar}
            active={upgradeButtonIsActive}
          >
            <Trans>Upgrade</Trans>
          </NavLink>
        )}

        {accountType === AccountType.BUSINESS_ADMIN && (
          <NavLink
            onClick={onNavigate}
            data-section="business"
            to="/biz"
            icon={SvgBusinessMenuItem}
            active={bizButtonIsActive}
          >
            {businessName || t`Business`}
          </NavLink>
        )}

        <NavLink
          onClick={onNavigate}
          data-section="settings"
          to="/web/account"
          icon={SvgSettingsMenuItem}
          active={settingsButtonIsActive}
        >
          <Trans>Settings</Trans>
        </NavLink>
      </NavSection>

      {referralsEnabled && (
        <NavSection>
          <NavLink
            onClick={onNavigate}
            data-section="referral"
            to="/web/refer-friend"
            icon={SvgGift}
            active={referIsActive}
          >
            <Trans>Invite a friend</Trans>
          </NavLink>
        </NavSection>
      )}
    </nav>
  )
})

function ConditionalOnboarding({
  enabled,
  task,
  children,
}: {
  enabled: boolean
  task: OnboardingPopupKeys
  children: React.ReactNode
}) {
  if (!enabled) {
    return <>{children}</>
  }
  return <Onboarding task={task}>{children}</Onboarding>
}
